import { useForm, useWatch } from "react-hook-form";
import {
  useGenerateReportsMutation,
  useGetReportCategoriesQuery,
} from "../../../services/api/reportsApi";
import { GenerateReportFormValues } from "../../../services/api/types/reports/models/GenerateReportsFormFields";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { ResidentModel } from "app/services/api/types/residents/models/ResidentModel";
import { useEffect, useRef } from "react";

interface Props {
  initialResident?: ResidentModel;
  onSuccess?: (newIds: number[]) => void;
}

export function useGenerateReport({ initialResident, onSuccess }: Props) {
  const { data: reportCategories, isLoading } = useGetReportCategoriesQuery();
  const [triggerGenerateReports] = useGenerateReportsMutation();
  const { showErrorToast } = useDisplayToast();

  const isFirstRender = useRef(true);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = useForm<GenerateReportFormValues>({
    defaultValues: {
      location_id: initialResident?.location?.id ?? undefined,
      resident_ids: initialResident?.id ? [initialResident.id] : [],
      start_date: undefined,
      end_date: undefined,
      categories: [],
    },
  });

  const locationSelected = useWatch({
    control,
    name: "location_id",
  });

  // Clear selected residents when location changes
  useEffect(() => {
    // Skip on initial render to avoid clearing pre-filled values
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Clear resident_ids when location changes
    setValue("resident_ids", []);
  }, [locationSelected, setValue]);

  const submit = handleSubmit(async ({ location_id, ...data }) => {
    try {
      const response = await triggerGenerateReports(data).unwrap();

      if (onSuccess) onSuccess(response.report_ids);
    } catch (error) {
      showErrorToast(error);
    }
  });

  return {
    control,
    reportCategories,
    isLoading,
    submit,
    isSubmitting,
  };
}
